/// <amd-module name="CodeCoverage/CodeCoverageCiChangedTab/FilesCoverageTable" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { ScreenBreakpoints } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { css, VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { Icon } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import {
    ColumnSorting,
    ColumnJustification,
    ITableBreakpoint,
    ITableColumn,
    ITableRow,
    ITableRowDetails,
    SimpleTableCell,
    sortItems,
    SortOrder,
    Table,
    TableLoadingRow
} from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

import * as Resources from "CodeCoverage/CodeCoverageCiChangedTab/Resources";
import { CoveredBarChart } from "CodeCoverage/ViewCommon/Components/CoveredBarChart";
import { IFileCoverageTableItem } from "CodeCoverage/ViewCommon/Contracts/Common";
import { getCoverageChangeIconName } from "CodeCoverage/ViewCommon/Helpers";

const tableBreakpoints: ITableBreakpoint[] = [
    {
        breakpoint: ScreenBreakpoints.xsmall,
        columnWidths: [-100, 140, 100, 0, 0]
    },
    {
        breakpoint: ScreenBreakpoints.small,
        columnWidths: [-100, 140, 100, 60, 60]
    },
    {
        breakpoint: ScreenBreakpoints.medium,
        columnWidths: [-100, 140, 100, 100, 100]
    }
];

export interface IFilesCoverageProps {
    itemsProvider: ArrayItemProvider<IFileCoverageTableItem | ObservableValue<IFileCoverageTableItem | undefined>>;
    loadMore?: () => void;
    onRowClick?: (event: React.SyntheticEvent<HTMLElement, Event>, row: IFileCoverageTableItem) => void;
}

export class FilesCoverageTable extends VssComponent<IFilesCoverageProps> {
    private tableColumns: ITableColumn<IFileCoverageTableItem>[];
    private sortedTableItemProvider: ObservableValue<ArrayItemProvider<IFileCoverageTableItem | ObservableValue<IFileCoverageTableItem | undefined>>>;

    constructor(props: IFilesCoverageProps, context: IVssComponentContext) {
        super(props, context);

        this.tableColumns = this._getColumns();
        this.sortedTableItemProvider = new ObservableValue(new ArrayItemProvider([]));
    }

    public render(): JSX.Element {
        this.sortedTableItemProvider = new ObservableValue(this.props.itemsProvider);

        return (
            <div>
                <Observer itemProvider={this.sortedTableItemProvider}>
                    {(props: { itemProvider: ArrayItemProvider<IFileCoverageTableItem> }) => (
                        <Table<IFileCoverageTableItem>
                            className="changed-files-coverage-table"
                            tableBreakpoints={tableBreakpoints}
                            columns={this.tableColumns}
                            showLines={true}
                            itemProvider={props.itemProvider}
                            behaviors={[this.sortingBehavior]}
                            onActivate={this._onRowActivate}
                            renderLoadingRow={(rowIndex: number, rowDetails: ITableRowDetails<IFileCoverageTableItem>) => {
                                this.props.loadMore && this.props.loadMore();
                                return <TableLoadingRow columns={this.tableColumns} details={rowDetails} key={rowIndex} rowIndex={rowIndex} />;
                            }}
                        />
                    )}
                </Observer>
            </div>
        );
    }

    private _onRowActivate = (event: React.SyntheticEvent<HTMLElement, Event>, row: ITableRow<IFileCoverageTableItem>): void => {
        if (this.props.onRowClick) {
            this.props.onRowClick(event, row.data);
        }
    };

    private _getColumns(): ITableColumn<IFileCoverageTableItem>[] {
        return [
            {
                id: "filePath",
                name: Resources.FilePath,
                minWidth: 100,
                width: new ObservableValue(-100),
                renderCell: this.renderFilePath
            },
            {
                id: "coveragePercentage",
                name: Resources.CoveragePercentage,
                minWidth: 100,
                width: new ObservableValue(140),
                justification: ColumnJustification.Right,
                renderCell: this.renderCoveragePercentageColumn,
                sortProps: { ariaLabelAscending: Resources.TableSortHightoLow, ariaLabelDescending: Resources.TableSortLowtoHigh }
            },
            {
                id: "coverageChangePercentage",
                name: Resources.ChangePercentage,
                width: new ObservableValue(100),
                justification: ColumnJustification.Right,
                renderCell: this.renderCoverageChangePercentageColumn,
                sortProps: {
                    sortOrder: SortOrder.ascending,
                    ariaLabelAscending: Resources.TableSortHightoLow,
                    ariaLabelDescending: Resources.TableSortLowtoHigh
                }
            },
            {
                id: "totalLines",
                name: Resources.TotalLines,
                width: new ObservableValue(100),
                justification: ColumnJustification.Right,
                renderCell: this.renderTotalLinesColumn,
                sortProps: { ariaLabelAscending: Resources.TableSortHightoLow, ariaLabelDescending: Resources.TableSortLowtoHigh }
            },
            {
                id: "notCoveredLines",
                name: Resources.NotCoveredLines,
                width: new ObservableValue(100),
                justification: ColumnJustification.Right,
                renderCell: this.renderNotCoveredLinesColumn,
                sortProps: { ariaLabelAscending: Resources.TableSortHightoLow, ariaLabelDescending: Resources.TableSortLowtoHigh }
            }
        ];
    }

    private renderFilePath = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IFileCoverageTableItem>,
        tableItem: IFileCoverageTableItem
    ): JSX.Element => {
        const { filePath } = tableItem;
        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={columnIndex}
                contentClassName={"bolt-list-cell-child coverage-table-file-path"}
            >
                <>
                    <Icon className="margin-right-8" iconName={"Page"} />
                    <Tooltip text={filePath} overflowOnly={true}>
                        <div className="text-ellipsis">{filePath}</div>
                    </Tooltip>
                </>
            </SimpleTableCell>
        );
    };

    private renderCoveragePercentageColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IFileCoverageTableItem>,
        tableItem: IFileCoverageTableItem
    ): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={columnIndex} contentClassName={"bolt-list-cell-child"}>
                <div className={"flex-row flex-grow cc-justify-space-between"}>
                    <CoveredBarChart coveredPercentage={tableItem.coveragePercentage} width={50} notCoveredCutoff={30} partiallyCoveredCutoff={70} />

                    <div className="padding-left-8">
                        {format(Resources.CoveragePercentageFormat, Math.abs(tableItem.coveragePercentage).toFixed(2))}
                    </div>
                </div>
            </SimpleTableCell>
        );
    };

    private renderCoverageChangePercentageColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IFileCoverageTableItem>,
        tableItem: IFileCoverageTableItem
    ): JSX.Element => {
        const { coverageChangePercentage } = tableItem;
        let coverageChangeIcon = getCoverageChangeIconName(coverageChangePercentage);

        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={columnIndex} contentClassName={"bolt-list-cell-child"}>
                <div className="flex-row flex-grow cc-chng-prct cc-justify-space-between">
                    <Icon
                        className={css("padding-right-8", coverageChangeIcon === "DashKey" ? "body-m" : "font-size")}
                        iconName={coverageChangeIcon}
                    />
                    <div>{format(Resources.CoveragePercentageFormat, Math.abs(coverageChangePercentage).toFixed(2))}</div>
                </div>
            </SimpleTableCell>
        );
    };

    private renderTotalLinesColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IFileCoverageTableItem>,
        tableItem: IFileCoverageTableItem
    ): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={columnIndex} contentClassName={"bolt-list-cell-child"}>
                {tableItem.totalLines.toLocaleString()}
            </SimpleTableCell>
        );
    };

    private renderNotCoveredLinesColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IFileCoverageTableItem>,
        tableItem: IFileCoverageTableItem
    ): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={columnIndex} contentClassName={"bolt-list-cell-child"}>
                {tableItem.notCoveredLines.toLocaleString()}
            </SimpleTableCell>
        );
    };

    private sortingBehavior = new ColumnSorting<IFileCoverageTableItem>(
        (columnIndex: number, proposedSortOrder: SortOrder, event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>) => {
            this.sortedTableItemProvider.value = new ArrayItemProvider(
                sortItems(columnIndex, proposedSortOrder, sortFunctions, this.tableColumns, this.props.itemsProvider.value)
            );
        }
    );
}

const sortFunctions = [
    // Name column doesn't need a sort function
    null,

    // Sort on Coverage Percentage column
    (item1: IFileCoverageTableItem, item2: IFileCoverageTableItem): number => {
        return item1.coveragePercentage - item2.coveragePercentage;
    },

    // Sort on Coverage Change Percentage column
    (item1: IFileCoverageTableItem, item2: IFileCoverageTableItem): number => {
        return item1.coverageChangePercentage - item2.coverageChangePercentage;
    },

    // Sort on Total lines column
    (item1: IFileCoverageTableItem, item2: IFileCoverageTableItem): number => {
        return item1.totalLines - item2.totalLines;
    },

    // Sort on Not covered lines column
    (item1: IFileCoverageTableItem, item2: IFileCoverageTableItem): number => {
        return item1.notCoveredLines - item2.notCoveredLines;
    }
];
